<template>
          <v-dialog :model-value="modelValue" @update:model-value="updateDialog" max-width="600">
            <v-card>
                <v-card-title class="text-h6">
                    {{ t('downloadReport') }}
                </v-card-title>
                <v-card-text>
                    <!-- Report Mode Selection -->
                    <v-btn-toggle
                        v-model="selectedMode"
                        mandatory
                        class="mb-4"
                        :disabled="generating"
                    >
                        <v-btn value="grid" :disabled="disabledModes.includes('grid')">
                            <v-icon start>mdi-grid</v-icon>
                            {{ t('grid') }}
                        </v-btn>
                        <v-btn value="matrix" :disabled="disabledModes.includes('matrix')">
                            <v-icon start>mdi-numeric</v-icon>
                            {{ t('numeric') }}
                        </v-btn>
                    </v-btn-toggle>

                    <!-- Report Type Selection - Only shown for Matrix mode -->
                    <v-select
                        v-if="selectedMode === 'matrix'"
                        v-model="selectedReportType"
                        :items="reportTypes"
                        :label="t('selectReportType')"
                        class="mb-4"
                        :disabled="generating"
                        item-title="title"
                        item-value="value"
                    >
                        <template v-slot:selection="{ item }">
                            <v-icon :icon="item.raw.icon" class="me-2" />
                            {{ item.raw.title }}
                        </template>
                        <template v-slot:item="{ props, item }">
                            <v-list-item v-bind="props">
                                <template v-slot:prepend>
                                    <v-icon :icon="item.raw.icon" class="me-2" />
                                </template>
                            </v-list-item>
                        </template>
                    </v-select>

                    <!-- File Format Selection -->
                    <v-select
                        v-model="selectedFileFormat"
                        :items="fileFormats"
                        :label="t('selectFileFormat')"
                        class="mb-4"
                        :disabled="generating || (selectedMode === 'matrix' && !selectedReportType)"
                        item-title="title"
                        item-value="value"
                    >
                        <template v-slot:selection="{ item }">
                            <v-icon :icon="item.raw.icon" class="me-2" />
                            {{ item.raw.title }}
                        </template>
                        <template v-slot:item="{ props, item }">
                            <v-list-item v-bind="props">
                                <template v-slot:prepend>
                                    <v-icon :icon="item.raw.icon" class="me-2" />
                                </template>
                            </v-list-item>
                        </template>
                    </v-select>

                    <!-- Page Size Selection - Only shown for applicable formats -->
                    <v-select
                        v-if="showPageSizeOption"
                        v-model="selectedPageSize"
                        :items="pageSizes"
                        :label="t('selectPageSize')"
                        class="mb-4"
                        :disabled="generating"
                        item-title="title"
                        item-value="value"
                    >
                        <template v-slot:selection="{ item }">
                            <v-icon icon="mdi-file-document-outline" class="me-2" />
                            {{ item.raw.title }}
                        </template>
                        <template v-slot:item="{ props }">
                            <v-list-item v-bind="props">
                                <template v-slot:prepend>
                                    <v-icon icon="mdi-file-document-outline" class="me-2" />
                                </template>
                            </v-list-item>
                        </template>
                    </v-select>

                    <!-- Group By / Order By Selection - opt-in (groupByOptions / orderByOptions), Grid mode only -->
                    <v-select
                        v-if="selectedMode === 'grid' && groupByOptions.length"
                        v-model="selectedGroupBy"
                        :items="groupByOptions"
                        :label="t('groupBy')"
                        class="mb-4"
                        :disabled="generating"
                        item-title="title"
                        item-value="value"
                    >
                        <template v-slot:selection="{ item }">
                            <v-icon icon="mdi-group" class="me-2" />
                            {{ item.raw.title }}
                        </template>
                        <template v-slot:item="{ props }">
                            <v-list-item v-bind="props">
                                <template v-slot:prepend>
                                    <v-icon icon="mdi-group" class="me-2" />
                                </template>
                            </v-list-item>
                        </template>
                    </v-select>

                    <v-select
                        v-if="selectedMode === 'grid' && orderByOptions.length"
                        v-model="selectedOrderBy"
                        :items="orderByOptions"
                        :label="t('orderBy')"
                        class="mb-4"
                        :disabled="generating"
                        item-title="title"
                        item-value="value"
                    >
                        <template v-slot:selection="{ item }">
                            <v-icon icon="mdi-sort" class="me-2" />
                            {{ item.raw.title }}
                        </template>
                        <template v-slot:item="{ props }">
                            <v-list-item v-bind="props">
                                <template v-slot:prepend>
                                    <v-icon icon="mdi-sort" class="me-2" />
                                </template>
                            </v-list-item>
                        </template>
                    </v-select>

                    <!-- Generate Report Button -->
                    <v-btn
                        block
                        color="primary"
                        :loading="generating"
                        :disabled="!isFormValid"
                        @click="requestReport"
                        class="mt-4"
                    >
                        <v-icon start icon="mdi-file-download"></v-icon>
                        {{ t('generateReport') }}
                    </v-btn>

                    <v-progress-linear
                        v-if="generating"
                        indeterminate
                        color="primary"
                        class="mt-4"
                    ></v-progress-linear>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn
                        color="grey"
                        variant="text"
                        @click="closeDialog"
                        :disabled="generating"
                    >
                        {{ t('cancel') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
</template>

<script src="./model.js"></script>
<style src="./style.css"></style>
